import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { Loader2 } from "lucide-react";
import { useAuth } from "../../../supabase/auth";
import { supabase } from "../../../supabase/supabase";
import { useOrganization } from "../../App";

type Simulator = {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  persona_id: string;
  assistant_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  transcript: any[];
  configuration: any;
  started_at: string | null;
  ended_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string | null;
};

type Persona = {
  id: string;
  name: string;
  description: string | null;
};

type Assistant = {
  id: string;
  name: string;
  description: string | null;
  assistant_type: string;
};

interface SimulatorFormProps {
  simulator: Simulator | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function SimulatorForm({ simulator, onSuccess, onCancel }: SimulatorFormProps) {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    persona_id: "",
    assistant_id: "",
    configuration: {
      max_duration: 300,
      max_turns: 20,
      auto_start: false,
    },
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (simulator) {
      setFormData({
        name: simulator.name,
        description: simulator.description || "",
        persona_id: simulator.persona_id,
        assistant_id: simulator.assistant_id,
        configuration: {
          max_duration: simulator.configuration?.max_duration || 300,
          max_turns: simulator.configuration?.max_turns || 20,
          auto_start: simulator.configuration?.auto_start || false,
        },
      });
    } else {
      // Reset form for new simulator
      setFormData({
        name: "",
        description: "",
        persona_id: "",
        assistant_id: "",
        configuration: {
          max_duration: 300,
          max_turns: 20,
          auto_start: false,
        },
      });
    }
    setErrors({});
  }, [simulator]);

  useEffect(() => {
    if (user && currentOrganization) {
      fetchPersonasAndAssistants();
    }
  }, [user, currentOrganization]);

  const fetchPersonasAndAssistants = async () => {
    setIsLoadingData(true);
    try {
      if (!currentOrganization) return;

      // Fetch personas
      const { data: personasData, error: personasError } = await supabase
        .from("personas")
        .select("id, name, description")
        .eq("organization_id", currentOrganization.id)
        .order("name");

      if (personasError) {
        console.error("Error fetching personas:", personasError);
        toast({
          title: "Error",
          description: "Failed to load personas.",
          variant: "destructive",
        });
      } else {
        setPersonas(personasData || []);
      }

      // Fetch assistants
      const { data: assistantsData, error: assistantsError } = await supabase
        .from("assistants")
        .select("id, name, description, assistant_type")
        .eq("organization_id", currentOrganization.id)
        .order("name");

      if (assistantsError) {
        console.error("Error fetching assistants:", assistantsError);
        toast({
          title: "Error",
          description: "Failed to load assistants.",
          variant: "destructive",
        });
      } else {
        setAssistants(assistantsData || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast({
        title: "Error",
        description: "Failed to load personas and assistants.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingData(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.persona_id) {
      newErrors.persona_id = "Please select a persona";
    }

    if (!formData.assistant_id) {
      newErrors.assistant_id = "Please select an assistant";
    }

    if (formData.configuration.max_duration < 60 || formData.configuration.max_duration > 3600) {
      newErrors.max_duration = "Duration must be between 60 and 3600 seconds";
    }

    if (formData.configuration.max_turns < 1 || formData.configuration.max_turns > 100) {
      newErrors.max_turns = "Turn limit must be between 1 and 100";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!currentOrganization || !user) {
      toast({
        title: "Error",
        description: "User authentication or organization required",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const now = new Date().toISOString();
      const simulatorData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        persona_id: formData.persona_id,
        assistant_id: formData.assistant_id,
        configuration: formData.configuration,
        organization_id: currentOrganization.id,
        updated_at: now,
        updated_by: user.id,
      };

      if (simulator) {
        // Update existing simulator
        const { error } = await supabase
          .from("simulators")
          .update(simulatorData)
          .eq("id", simulator.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Simulator updated successfully",
        });
      } else {
        // Create new simulator
        const { error } = await supabase.from("simulators").insert({
          ...simulatorData,
          status: 'pending',
          transcript: [],
          created_by: user.id,
          created_at: now,
        });

        if (error) throw error;

        toast({
          title: "Success",
          description: "Simulator created successfully",
        });
      }

      onSuccess();
    } catch (error: any) {
      console.error("Error saving simulator:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save simulator",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingData) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin mr-2" />
        <span>Loading personas and assistants...</span>
      </div>
    );
  }

  if (personas.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground mb-4">
          You need to create at least one persona before creating a simulator.
        </p>
        <Button onClick={() => window.location.href = '/personas'}>
          Create Persona
        </Button>
      </div>
    );
  }

  if (assistants.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground mb-4">
          You need to create at least one assistant before creating a simulator.
        </p>
        <Button onClick={() => window.location.href = '/assistants'}>
          Create Assistant
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Basic Information</h3>
        
        <div className="grid grid-cols-1 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Customer Support Simulation"
              className={errors.name ? "border-destructive" : ""}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the purpose and scenario of this simulation..."
              rows={3}
            />
          </div>
        </div>
      </div>

      {/* Simulation Configuration */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Simulation Configuration</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="persona_id">Persona *</Label>
            <Select
              value={formData.persona_id}
              onValueChange={(value) => setFormData({ ...formData, persona_id: value })}
            >
              <SelectTrigger className={errors.persona_id ? "border-destructive" : ""}>
                <SelectValue placeholder="Select a persona" />
              </SelectTrigger>
              <SelectContent>
                {personas.map((persona) => (
                  <SelectItem key={persona.id} value={persona.id}>
                    {persona.name}
                    {persona.description && (
                      <span className="text-sm text-muted-foreground ml-2">
                        - {persona.description.substring(0, 50)}...
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.persona_id && (
              <p className="text-sm text-destructive">{errors.persona_id}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="assistant_id">Assistant *</Label>
            <Select
              value={formData.assistant_id}
              onValueChange={(value) => setFormData({ ...formData, assistant_id: value })}
            >
              <SelectTrigger className={errors.assistant_id ? "border-destructive" : ""}>
                <SelectValue placeholder="Select an assistant" />
              </SelectTrigger>
              <SelectContent>
                {assistants.map((assistant) => (
                  <SelectItem key={assistant.id} value={assistant.id}>
                    {assistant.name}
                    <span className="text-sm text-muted-foreground ml-2">
                      ({assistant.assistant_type === 'multi_agent' ? 'Multi-Agent' : 'Single Agent'})
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.assistant_id && (
              <p className="text-sm text-destructive">{errors.assistant_id}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="max_duration">Max Duration (seconds) *</Label>
            <Input
              id="max_duration"
              type="number"
              min="60"
              max="3600"
              value={formData.configuration.max_duration}
              onChange={(e) => setFormData({ 
                ...formData, 
                configuration: {
                  ...formData.configuration,
                  max_duration: parseInt(e.target.value) || 300 
                }
              })}
              className={errors.max_duration ? "border-destructive" : ""}
            />
            {errors.max_duration && (
              <p className="text-sm text-destructive">{errors.max_duration}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Maximum duration for the simulation (60-3600 seconds)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="max_turns">Max Conversation Turns *</Label>
            <Input
              id="max_turns"
              type="number"
              min="1"
              max="100"
              value={formData.configuration.max_turns}
              onChange={(e) => setFormData({ 
                ...formData, 
                configuration: {
                  ...formData.configuration,
                  max_turns: parseInt(e.target.value) || 20 
                }
              })}
              className={errors.max_turns ? "border-destructive" : ""}
            />
            {errors.max_turns && (
              <p className="text-sm text-destructive">{errors.max_turns}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Maximum number of back-and-forth exchanges (1-100)
            </p>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-2 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {simulator ? "Updating..." : "Creating..."}
            </>
          ) : (
            simulator ? "Update Simulator" : "Create Simulator"
          )}
        </Button>
      </div>
    </form>
  );
}